import React, { useState, useEffect, useCallback } from 'react';
import './CertificateGeneration.css';
import collegeConfig from '../../config/collegeConfig';
import { jsPDF } from 'jspdf';
import { savePDF } from '../../utils/savePDF';
import {
  FaAward,
  FaUserGraduate,
  FaCheckCircle,
  FaDownload,
  FaRedo,
  FaSearch,
  FaChevronDown,
  FaChevronUp,
  FaArrowLeft,
  FaSpinner
} from 'react-icons/fa';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
};

const getBase64Image = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

// ─── PDF Generator ────────────────────────────────────────────────────────────
const generateCertificatePDF = async (cert, collegeName, logoSrc) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();   // 297
  const H = doc.internal.pageSize.getHeight();  // 210

  const MAROON  = [142, 44,  44];
  const GOLD    = [212, 177, 90];
  const NAVY    = [31,  60,  136];
  const DARKGRAY= [60,  60,  60];

  // Outer decorative border
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(3);
  doc.rect(8, 8, W - 16, H - 16);
  doc.setLineWidth(1);
  doc.rect(12, 12, W - 24, H - 24);

  // Top maroon banner
  doc.setFillColor(...MAROON);
  doc.rect(12, 12, W - 24, 52, 'F');

  // College logo — centered
  const logoBase64 = await getBase64Image(logoSrc);
  if (logoBase64) {
    try { doc.addImage(logoBase64, 'PNG', W / 2 - 14, 13, 28, 28); }
    catch (_) { /* skip */ }
  }

  // College name — centered below logo
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  const nameLines = doc.splitTextToSize(collegeName, W - 60);
  doc.text(nameLines, W / 2, 48, { align: 'center' });

  // Gold separator
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(1.2);
  doc.line(15, 66, W - 15, 66);

  // "CERTIFICATE" title
  doc.setTextColor(...MAROON);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(28);
  doc.text('CERTIFICATE', W / 2, 82, { align: 'center' });

  // "of Course Completion" subtitle
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(12);
  doc.setTextColor(...GOLD);
  doc.text('of Course Completion', W / 2, 90, { align: 'center' });

  // Course name badge
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...NAVY);
  doc.text(cert.courseName || '', W / 2, 99, { align: 'center' });

  // Certificate body text
  const startYear  = cert.batchStartYear || '____';
  const endYear    = cert.batchEndYear   || '____';
  const level      = cert.courseLevel    || 'MD/MS';
  const courseName = cert.courseName     || '';

  const bodyText =
    `This is to certify that DR. ${cert.studentName} has successfully completed postgraduate training ` +
    `from ${startYear} to ${endYear} for ${level} in ${courseName} from ${collegeName}. ` +
    `The work and academic activities recorded in the Logbook are as per hospital records and have been ` +
    `carried out under guidance of faculty members of the Department of ${courseName}.`;

  doc.setFont('times', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(...DARKGRAY);
  const bodyLines = doc.splitTextToSize(bodyText, W - 60);
  doc.text(bodyLines, W / 2, 111, { align: 'center', lineHeightFactor: 1.6 });

  // Gold divider above signatures
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.8);
  doc.line(15, H - 52, W - 15, H - 52);

  // Signature columns
  const col1 = 50;
  const col2 = W / 2;
  const col3 = W - 50;
  const sigY = H - 32;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...MAROON);

  // Guide
  doc.text('Guide', col1, sigY - 12, { align: 'center' });
  doc.setDrawColor(...DARKGRAY);
  doc.setLineWidth(0.4);
  doc.line(col1 - 30, sigY - 4, col1 + 30, sigY - 4);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...DARKGRAY);
  doc.text(cert.guideName || '', col1, sigY, { align: 'center' });

  // Date
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...MAROON);
  doc.text('Date', col2, sigY - 12, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...DARKGRAY);
  doc.text(formatDate(cert.generatedAt), col2, sigY, { align: 'center' });

  // HOD
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...MAROON);
  doc.text('Head of Department', col3, sigY - 12, { align: 'center' });
  doc.setDrawColor(...DARKGRAY);
  doc.setLineWidth(0.4);
  doc.line(col3 - 35, sigY - 4, col3 + 35, sigY - 4);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...DARKGRAY);
  doc.text(cert.hodName || '', col3, sigY, { align: 'center' });

  // Certificate number (bottom center)
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text(`Certificate No: ${cert.certificateNumber || ''}`, W / 2, H - 16, { align: 'center' });

  await savePDF(doc, `Certificate_${cert.studentName?.replace(/\s+/g, '_')}.pdf`);
};

// ─── E-Logbook Completion Certificate PDF Generator ────────────────────────────
const generateElogbookCertificatePDF = async (cert, collegeName, logoSrc) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();

  const MAROON  = [142, 44,  44];
  const GOLD    = [212, 177, 90];
  const NAVY    = [31,  60,  136];
  const DARKGRAY= [60,  60,  60];

  doc.setDrawColor(...GOLD);
  doc.setLineWidth(3);
  doc.rect(8, 8, W - 16, H - 16);
  doc.setLineWidth(1);
  doc.rect(12, 12, W - 24, H - 24);

  doc.setFillColor(...MAROON);
  doc.rect(12, 12, W - 24, 52, 'F');

  const logoBase64 = await getBase64Image(logoSrc);
  if (logoBase64) {
    try { doc.addImage(logoBase64, 'PNG', W / 2 - 14, 13, 28, 28); }
    catch (_) { /* skip */ }
  }

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  const nameLines = doc.splitTextToSize(collegeName, W - 60);
  doc.text(nameLines, W / 2, 48, { align: 'center' });

  doc.setDrawColor(...GOLD);
  doc.setLineWidth(1.2);
  doc.line(15, 66, W - 15, 66);

  doc.setTextColor(...MAROON);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(28);
  doc.text('CERTIFICATE', W / 2, 82, { align: 'center' });

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(12);
  doc.setTextColor(...GOLD);
  doc.text('of E-Logbook Completion', W / 2, 90, { align: 'center' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...NAVY);
  doc.text(cert.courseName || '', W / 2, 99, { align: 'center' });

  const startYear  = cert.batchStartYear || '____';
  const endYear    = cert.batchEndYear   || '____';
  const level      = cert.courseLevel    || 'MD/MS';
  const courseName = cert.courseName     || '';

  const bodyText =
    `This is to certify that DR. ${cert.studentName}, Postgraduate student of ${level} in ${courseName} at ${collegeName}, ` +
    `has successfully and satisfactorily maintained and completed the E-Logbook covering all academic activities, clinical ` +
    `postings, procedures, and case discussions as mandated by the curriculum, during the period ${startYear} to ${endYear}. ` +
    `The entries recorded therein have been duly reviewed and verified by the concerned faculty.`;

  doc.setFont('times', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(...DARKGRAY);
  const bodyLines = doc.splitTextToSize(bodyText, W - 60);
  doc.text(bodyLines, W / 2, 111, { align: 'center', lineHeightFactor: 1.6 });

  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.8);
  doc.line(15, H - 52, W - 15, H - 52);

  const col1 = 50;
  const col2 = W / 2;
  const col3 = W - 50;
  const sigY = H - 32;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...MAROON);
  doc.text('Guide', col1, sigY - 12, { align: 'center' });
  doc.setDrawColor(...DARKGRAY);
  doc.setLineWidth(0.4);
  doc.line(col1 - 30, sigY - 4, col1 + 30, sigY - 4);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...DARKGRAY);
  doc.text(cert.guideName || '', col1, sigY, { align: 'center' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...MAROON);
  doc.text('Date', col2, sigY - 12, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...DARKGRAY);
  doc.text(formatDate(cert.generatedAt), col2, sigY, { align: 'center' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...MAROON);
  doc.text('Head of Department', col3, sigY - 12, { align: 'center' });
  doc.setDrawColor(...DARKGRAY);
  doc.setLineWidth(0.4);
  doc.line(col3 - 35, sigY - 4, col3 + 35, sigY - 4);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...DARKGRAY);
  doc.text(cert.hodName || '', col3, sigY, { align: 'center' });

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text(`Certificate No: ${cert.certificateNumber || ''}`, W / 2, H - 16, { align: 'center' });

  await savePDF(doc, `Elogbook_Certificate_${cert.studentName?.replace(/\s+/g, '_')}.pdf`);
};

// ─── Component ────────────────────────────────────────────────────────────────
const CertificateGeneration = () => {
  const [batches, setBatches] = useState({});
  const [courseId, setCourseId] = useState(null);
  const [course, setCourse] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedBatch, setExpandedBatch] = useState(null);
  const [search, setSearch] = useState('');
  // Per-student state for guide selection & generation
  const [studentGuides, setStudentGuides] = useState({}); // { studentId: guideId }
  const [generating, setGenerating] = useState({}); // { studentId: true/false }
  const [downloadingId, setDownloadingId] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  // Per-student state for E-Logbook certificate guide selection & generation
  const [studentElogGuides, setStudentElogGuides] = useState({}); // { studentId: guideId }
  const [generatingElog, setGeneratingElog] = useState({}); // { studentId: true/false }
  const [downloadingElogId, setDownloadingElogId] = useState(null);

  const token = localStorage.getItem('token');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [studentsRes, teachersRes] = await Promise.all([
        fetch(`${collegeConfig.api.baseURL}/certificates/hod/students`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${collegeConfig.api.baseURL}/certificates/hod/teachers`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      const studentsData = await studentsRes.json();
      const teachersData = await teachersRes.json();

      if (studentsData.success) {
        setBatches(studentsData.batches || {});
        setCourseId(studentsData.courseId);
        setCourse(studentsData.course);
        // Auto-expand first batch
        const firstBatch = Object.keys(studentsData.batches || {})[0];
        if (firstBatch) setExpandedBatch(firstBatch);
      } else {
        setError(studentsData.message || 'Failed to load students');
      }

      if (teachersData.success) {
        setTeachers(teachersData.teachers || []);
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGenerateCertificate = async (student) => {
    const guideId = studentGuides[student._id] || '';
    setGenerating(prev => ({ ...prev, [student._id]: true }));
    setError('');
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/certificates/generate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ studentId: student._id, guideId })
      });
      const data = await res.json();
      if (data.success) {
        setSuccessMsg(`Certificate generated for ${student.name}!`);
        setTimeout(() => setSuccessMsg(''), 4000);
        await fetchData();
      } else {
        setError(data.message || 'Failed to generate certificate');
      }
    } catch (err) {
      setError('Network error while generating certificate.');
    } finally {
      setGenerating(prev => ({ ...prev, [student._id]: false }));
    }
  };

  const handleDownload = async (student) => {
    setDownloadingId(student.certificateId || student._id);
    try {
      await generateCertificatePDF(
        student.certData || {
          studentName: student.name,
          certificateNumber: student.certificateNumber,
          generatedAt: new Date().toISOString()
        },
        collegeConfig.collegeName,
        collegeConfig.logo
      );
    } finally {
      setDownloadingId(null);
    }
  };

  const handleGenerateElogCertificate = async (student) => {
    const guideId = studentElogGuides[student._id] || '';
    setGeneratingElog(prev => ({ ...prev, [student._id]: true }));
    setError('');
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/certificates/elogbook/generate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ studentId: student._id, guideId })
      });
      const data = await res.json();
      if (data.success) {
        setSuccessMsg(`E-Logbook certificate generated for ${student.name}!`);
        setTimeout(() => setSuccessMsg(''), 4000);
        await fetchData();
      } else {
        setError(data.message || 'Failed to generate E-Logbook certificate');
      }
    } catch (err) {
      setError('Network error while generating E-Logbook certificate.');
    } finally {
      setGeneratingElog(prev => ({ ...prev, [student._id]: false }));
    }
  };

  const handleDownloadElog = async (student) => {
    setDownloadingElogId(student.elogbookCertificateId || student._id);
    try {
      await generateElogbookCertificatePDF(
        student.elogbookCertData || {
          studentName: student.name,
          certificateNumber: '',
          generatedAt: new Date().toISOString()
        },
        collegeConfig.collegeName,
        collegeConfig.logo
      );
    } finally {
      setDownloadingElogId(null);
    }
  };

  // Filter students by search
  const filteredBatches = {};
  for (const [batch, students] of Object.entries(batches)) {
    const filtered = students.filter(s =>
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      (s.registrationNumber || '').toLowerCase().includes(search.toLowerCase())
    );
    if (filtered.length > 0) filteredBatches[batch] = filtered;
  }

  const totalStudents = Object.values(batches).flat().length;
  const certifiedCount = Object.values(batches).flat().filter(s => s.hasCertificate).length;

  if (loading) {
    return (
      <div className="certgen-loading">
        <FaSpinner className="certgen-spinner" />
        <p>Loading certificate data...</p>
      </div>
    );
  }

  return (
    <div className="certgen-container">
      {/* Header */}
      <div className="certgen-header">
        <div className="certgen-header-icon">
          <FaAward />
        </div>
        <div>
          <h2 className="certgen-title">Certificate Generation</h2>
          <p className="certgen-subtitle">
            {course ? `${course.name} (${course.code})` : 'Loading course...'}
          </p>
        </div>
      </div>

      {/* Summary stats */}
      <div className="certgen-stats">
        <div className="certgen-stat-card">
          <span className="certgen-stat-num">{totalStudents}</span>
          <span className="certgen-stat-label">Total Students</span>
        </div>
        <div className="certgen-stat-card certgen-stat-green">
          <span className="certgen-stat-num">{certifiedCount}</span>
          <span className="certgen-stat-label">Certificates Generated</span>
        </div>
        <div className="certgen-stat-card certgen-stat-amber">
          <span className="certgen-stat-num">{totalStudents - certifiedCount}</span>
          <span className="certgen-stat-label">Pending</span>
        </div>
      </div>

      {successMsg && (
        <div className="certgen-success-banner">
          <FaCheckCircle /> {successMsg}
        </div>
      )}

      {error && (
        <div className="certgen-error-banner">{error}</div>
      )}

      {/* Search */}
      <div className="certgen-search-bar">
        <FaSearch className="certgen-search-icon" />
        <input
          type="text"
          placeholder="Search student by name or reg. number..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="certgen-search-input"
        />
      </div>

      {/* Batch sections */}
      {Object.keys(filteredBatches).length === 0 ? (
        <div className="certgen-empty">
          <FaUserGraduate size={48} />
          <p>No students found</p>
        </div>
      ) : (
        Object.entries(filteredBatches).map(([batch, students]) => (
          <div key={batch} className="certgen-batch-card">
            <button
              className="certgen-batch-header"
              onClick={() => setExpandedBatch(expandedBatch === batch ? null : batch)}
            >
              <span className="certgen-batch-name">Batch: {batch}</span>
              <span className="certgen-batch-meta">
                {students.filter(s => s.hasCertificate).length}/{students.length} certified
              </span>
              {expandedBatch === batch ? <FaChevronUp /> : <FaChevronDown />}
            </button>

            {expandedBatch === batch && (
              <div className="certgen-student-list">
                {students.map(student => (
                  <div key={student._id} className="certgen-student-row">
                    <div className="certgen-student-info">
                      <FaUserGraduate className="certgen-student-icon" />
                      <div>
                        <p className="certgen-student-name">{student.name}</p>
                        <p className="certgen-student-reg">{student.registrationNumber || '—'}</p>
                      </div>
                      {student.hasCertificate && (
                        <span className="certgen-badge-generated">
                          <FaCheckCircle /> Generated
                        </span>
                      )}
                      {student.hasElogbookCertificate && (
                        <span className="certgen-badge-generated">
                          <FaCheckCircle /> E-Logbook Cert. Generated
                        </span>
                      )}
                    </div>

                    <div className="certgen-student-actions-group">
                    <div className="certgen-student-actions">
                      <span className="certgen-action-label">Course Completion</span>
                      {/* Guide selector */}
                      <select
                        className="certgen-guide-select"
                        value={studentGuides[student._id] || ''}
                        onChange={e =>
                          setStudentGuides(prev => ({ ...prev, [student._id]: e.target.value }))
                        }
                      >
                        <option value="">Select Guide (optional)</option>
                        {teachers.map(t => (
                          <option key={t._id} value={t._id}>{t.name}</option>
                        ))}
                      </select>

                      {/* Generate / Regenerate button */}
                      <button
                        className={`certgen-btn ${student.hasCertificate ? 'certgen-btn-regen' : 'certgen-btn-generate'}`}
                        onClick={() => handleGenerateCertificate(student)}
                        disabled={!!generating[student._id]}
                      >
                        {generating[student._id] ? (
                          <FaSpinner className="certgen-btn-spinner" />
                        ) : student.hasCertificate ? (
                          <><FaRedo /> Regenerate</>
                        ) : (
                          <><FaAward /> Generate</>
                        )}
                      </button>

                      {/* Download button (only if generated) */}
                      {student.hasCertificate && (
                        <button
                          className="certgen-btn certgen-btn-download"
                          onClick={() => handleDownload(student)}
                          disabled={downloadingId === student.certificateId || downloadingId === student._id}
                        >
                          {downloadingId === student.certificateId ? (
                            <FaSpinner className="certgen-btn-spinner" />
                          ) : (
                            <><FaDownload /> PDF</>
                          )}
                        </button>
                      )}
                    </div>

                    <div className="certgen-student-actions">
                      <span className="certgen-action-label">E-Logbook</span>
                      {/* E-Logbook guide selector */}
                      <select
                        className="certgen-guide-select"
                        value={studentElogGuides[student._id] || ''}
                        onChange={e =>
                          setStudentElogGuides(prev => ({ ...prev, [student._id]: e.target.value }))
                        }
                      >
                        <option value="">Select Guide (optional)</option>
                        {teachers.map(t => (
                          <option key={t._id} value={t._id}>{t.name}</option>
                        ))}
                      </select>

                      {/* Generate / Regenerate E-Logbook certificate button */}
                      <button
                        className={`certgen-btn ${student.hasElogbookCertificate ? 'certgen-btn-regen' : 'certgen-btn-generate'}`}
                        onClick={() => handleGenerateElogCertificate(student)}
                        disabled={!!generatingElog[student._id]}
                      >
                        {generatingElog[student._id] ? (
                          <FaSpinner className="certgen-btn-spinner" />
                        ) : student.hasElogbookCertificate ? (
                          <><FaRedo /> Regenerate E-Logbook Cert.</>
                        ) : (
                          <><FaAward /> Generate E-Logbook Cert.</>
                        )}
                      </button>

                      {/* Download button (only if generated) */}
                      {student.hasElogbookCertificate && (
                        <button
                          className="certgen-btn certgen-btn-download"
                          onClick={() => handleDownloadElog(student)}
                          disabled={downloadingElogId === student.elogbookCertificateId || downloadingElogId === student._id}
                        >
                          {downloadingElogId === student.elogbookCertificateId ? (
                            <FaSpinner className="certgen-btn-spinner" />
                          ) : (
                            <><FaDownload /> PDF</>
                          )}
                        </button>
                      )}
                    </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
};

export default CertificateGeneration;
